import { useTranslation } from "react-i18next";
import React, { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  isValidDollarAmount,
  isValidPercentageAmount,
  dollarsToCents,
  percentageToDollarAmount,
  dollarAmountToPercentage,
} from "@/lib/price-utils";

type DiscountMode = "percentage" | "dollar";

interface DiscountInputProps {
  discountPercentage: number;
  onDiscountPercentageChange: (value: number) => void;
  totalAmount: number;
  className?: string;
}

export function DiscountInput({
  discountPercentage,
  onDiscountPercentageChange,
  totalAmount,
  className = "",
}: DiscountInputProps) {
  const { t } = useTranslation();
  const [discountMode, setDiscountMode] = useState<DiscountMode>("percentage");
  const [percentageStr, setPercentageStr] = useState<string>(
    discountPercentage > 0 ? discountPercentage.toString() : "",
  );
  const [dollarStr, setDollarStr] = useState<string>(() => {
    const totalCents = dollarsToCents(totalAmount);
    const dollarAmount = percentageToDollarAmount(
      discountPercentage,
      totalCents,
    );
    return dollarAmount > 0 ? dollarAmount.toString() : "";
  });

  // Store the fixed value based on mode
  const [fixedDollarAmount, setFixedDollarAmount] = useState<number>(0);

  // Track the last prop values to detect external changes (initialize with sentinel values)
  const lastPropValues = React.useRef({
    discountPercentage: -1,
    totalAmount: -1,
  });

  // Sync with prop changes only when props actually changed externally
  useEffect(() => {
    const propsChanged =
      lastPropValues.current.discountPercentage !== discountPercentage ||
      lastPropValues.current.totalAmount !== totalAmount;

    if (propsChanged) {
      if (discountMode === "percentage") {
        // In percentage mode, keep percentage fixed, recalculate dollar amount
        setPercentageStr(
          discountPercentage > 0 ? discountPercentage.toString() : "",
        );
        const totalCents = dollarsToCents(totalAmount);
        const dollarAmount = percentageToDollarAmount(
          discountPercentage,
          totalCents,
        );
        setDollarStr(dollarAmount > 0 ? dollarAmount.toString() : "");
      } else {
        // In dollar mode, keep dollar amount fixed, recalculate percentage
        const totalCents = dollarsToCents(totalAmount);
        const newPercentage = dollarAmountToPercentage(
          fixedDollarAmount,
          totalCents,
        );
        setPercentageStr(newPercentage > 0 ? newPercentage.toFixed(2) : "");
        onDiscountPercentageChange(newPercentage);
      }

      // Update the ref to track current prop values
      lastPropValues.current = { discountPercentage, totalAmount };
    }
  }, [
    discountPercentage,
    totalAmount,
    discountMode,
    fixedDollarAmount,
    onDiscountPercentageChange,
  ]);

  const handlePercentageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    if (isValidPercentageAmount(value)) {
      const numericValue = value === "" ? 0 : parseFloat(value);

      if (numericValue <= 100) {
        setPercentageStr(value);

        // In percentage mode, calculate dollar amount for display
        const totalCents = dollarsToCents(totalAmount);
        const dollarAmount = percentageToDollarAmount(numericValue, totalCents);
        setDollarStr(dollarAmount > 0 ? dollarAmount.toString() : "");

        // Update the ref to prevent unnecessary re-sync when parent updates
        lastPropValues.current = {
          discountPercentage: numericValue,
          totalAmount,
        };
        onDiscountPercentageChange(numericValue);
      }
    }
  };

  const handleDollarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    if (isValidDollarAmount(value)) {
      const numericValue = value === "" ? 0 : parseFloat(value);

      if (numericValue <= totalAmount) {
        setDollarStr(value);
        setFixedDollarAmount(numericValue); // Store the fixed dollar amount

        // In dollar mode, calculate percentage for the form
        const totalCents = dollarsToCents(totalAmount);
        const percentage = dollarAmountToPercentage(numericValue, totalCents);
        setPercentageStr(percentage > 0 ? percentage.toFixed(2) : "");

        // Update the ref to prevent unnecessary re-sync when parent updates
        lastPropValues.current = {
          discountPercentage: percentage,
          totalAmount,
        };
        onDiscountPercentageChange(percentage);
      }
    }
  };

  const handleModeChange = (mode: DiscountMode) => {
    setDiscountMode(mode);

    if (mode === "dollar") {
      // When switching to dollar mode, store the current dollar amount as fixed
      const totalCents = dollarsToCents(totalAmount);
      const currentDollarAmount = percentageToDollarAmount(
        discountPercentage,
        totalCents,
      );
      setFixedDollarAmount(currentDollarAmount);
      setDollarStr(
        currentDollarAmount > 0 ? currentDollarAmount.toString() : "",
      );
    }
  };

  return (
    <div className={className}>
      {/* Mode Selector */}
      <div className="flex items-center justify-between mb-3 p-2 bg-gray-50 rounded-md">
        <span
          className={`text-sm font-medium transition-colors ${discountMode === "percentage" ? "text-blue-600" : "text-gray-500"}`}
        >
          {t("components.quotes.discountInput.percentage")}
        </span>
        <Switch
          checked={discountMode === "dollar"}
          onCheckedChange={(checked) =>
            handleModeChange(checked ? "dollar" : "percentage")
          }
        />
        <span
          className={`text-sm font-medium transition-colors ${discountMode === "dollar" ? "text-blue-600" : "text-gray-500"}`}
        >
          {t("components.quotes.discountInput.amount")}
        </span>
      </div>

      <div className="flex gap-2">
        {/* Percentage Input */}
        <div className="flex-1 relative">
          <Input
            type="text"
            value={percentageStr}
            onChange={handlePercentageChange}
            placeholder="0"
            className="pr-6"
            max="100"
            disabled={discountMode !== "percentage"}
          />
          <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
            <span className="text-gray-500">%</span>
          </div>
        </div>

        {/* Dollar Input */}
        <div className="flex-1 relative">
          <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
            <span className="text-gray-500">$</span>
          </div>
          <Input
            type="text"
            value={dollarStr}
            onChange={handleDollarChange}
            placeholder="0.00"
            className="pl-6"
            max={totalAmount.toFixed(2)}
            disabled={discountMode !== "dollar"}
          />
        </div>
      </div>
      <p className="text-xs text-muted-foreground mt-1">
        {discountMode === "percentage"
          ? t("components.quotes.discountInput.percentageStaysFixedWhen")
          : t("components.quotes.discountInput.dollarAmountStaysFixed")}
      </p>
    </div>
  );
}
